import React, {Component, ComponentProps} from 'react';

import {AsyncHandlerOf, Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {UnreachableError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {LightUserThemeProvider} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  findSendWarnings,
  MessageWarning,
  MessageWarningTypesEnum,
} from '../../../helpers/models/messageHelpers';
import {AttachmentStatusesEnum} from '../../../models/api/attachmentModel';
import {ChannelModel} from '../../../models/api/channelModel';
import {ConversationModel} from '../../../models/api/conversationModel';
import {
  DraftMessageModel,
  MessageStatusesEnum,
  MessageVisualTypesEnum,
  SendMessageOptionsModel,
} from '../../../models/api/messageModel';
import {SearchTagModel} from '../../../models/api/searchTagModel';
import {TagModel} from '../../../models/api/tagModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {fetchMostRecentExternalShareFiles} from '../../../services/electron/electronBridgeOutbound';
import {
  ConversationComposerWarningDialogTypesEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../services/tracking';
import {VariableInterpolator} from '../../../services/variableInterpolation/variableInterpolator';
import {AttachmentFileUploader} from '../../common/attachments/attachmentsTypes';
import {TagTemplate} from '../../common/dropdowns/tags/tagEditorBody';
import {RawVariable} from '../../common/editor/plugins/text/answerVariablePlugin';
import {DraftMessageBannerContainer} from '../viewer/activity/message/banner/draftMessageBannerContainer';
import {SharedDraftMessageBannerContainer} from '../viewer/activity/message/banner/sharedDraftMessageBannerContainer';
import {MessageBase} from '../viewer/activity/message/messageBase';
import {ChatComposer, ChatComposerPublicProps} from './chatComposer';
import {ComposerWarningsDialog} from './composerWarningsDialog';
import {EmailComposerContainer} from './emailComposerContainer';
import {ShareMessage} from './share/shareButtonCoordinator';
import {SendMessage} from './toolbar/buttons/sendToolbarButton';
import {TweetComposer, TweetComposerPublicProps} from './tweetComposer';

enum WarningClickActionsEnum {
  CANCEL = 'cancel',
  SEND = 'send',
}

/*
 * Props.
 */

type EmailComposerContainerProps = Omit<
  ComponentProps<typeof EmailComposerContainer>,
  'hasRawVariables' | 'onRawVariables' | 'onNewRawVariable'
>;
type SpecificComposerProps = EmailComposerContainerProps & TweetComposerPublicProps & ChatComposerPublicProps;
type OmittedSpecificComposerProps = Omit<
  SpecificComposerProps,
  'attemptedSendDraftAttachments' | 'dismissAttemptedSendDraftAttachments' | 'onPaste'
>;

export interface ComposerProps extends OmittedSpecificComposerProps {
  conversation: ConversationModel | null;
  channel: ChannelModel;
  message: DraftMessageModel;
  isMessageShared: boolean;
  interpolator: VariableInterpolator;
  createTag: AsyncHandlerOf<TagTemplate>;
  tagMessage: HandlerOf<SearchTagModel>;
  untagMessage: HandlerOf<TagModel>;
  selectedTagIds: ReadonlyArray<number>;
  shouldWarnManyRecipients: boolean;
  shouldWarnMissingAttachments: boolean;
  shouldDynamicallyGrowComposer?: boolean;
  user: TeammateModel;
  author?: TeammateModel;
  onRequestClose?: Handler;
  onPopout?: Handler;
  isComposerPopout: boolean;
  isComposerPopup: boolean;
  isComposerStandalone: boolean;
  shareMessage: ShareMessage;
  hasRawVariablesWarningFeatureFlag: boolean;
  onFileUpload: AttachmentFileUploader;
  shouldAttachExternalResources: boolean;
}

/*
 * Component.
 */

interface ComposerSendAttempt {
  sendOptions: Partial<SendMessageOptionsModel> | undefined;
  warnings: ReadonlyArray<MessageWarning>;
}

interface ComposerState {
  sendAttempt?: ComposerSendAttempt;
  attemptedSendDraftAttachments: boolean;
  rawVariables: readonly RawVariable[];
}

export class Composer extends Component<ComposerProps, ComposerState> {
  constructor(props: ComposerProps) {
    super(props);
    this.state = {
      attemptedSendDraftAttachments: false,
      rawVariables: [],
    };
  }

  /*
   * Event handlers.
   */

  private readonly onWarningsRequestClose = (
    {warnings}: ComposerSendAttempt,
    action: WarningClickActionsEnum,
  ) => {
    this.logWarningsDismiss(warnings, action);
    this.setState({sendAttempt: undefined});
  };

  private readonly onWarningsSendAnywayClick = (sendAttempt: ComposerSendAttempt) => {
    this.onWarningsRequestClose(sendAttempt, WarningClickActionsEnum.SEND);
    this.props.sendMessage(sendAttempt.sendOptions);
  };

  private readonly onRawVariables = (rawVariables: readonly RawVariable[]) => {
    this.setState({rawVariables});
  };

  private readonly onNewRawVariable = (rawVariable: RawVariable) => {
    this.setState((state) => ({rawVariables: [...state.rawVariables, rawVariable]}));
  };

  private readonly dismissAttemptedSendDraftAttachments = () => {
    this.setState({attemptedSendDraftAttachments: false});
  };

  private readonly sendMessage: SendMessage = (sendOptions) => {
    const {
      message,
      recipients,
      sendMessage,
      attachments,
      shouldWarnManyRecipients,
      shouldWarnMissingAttachments,
      hasRawVariablesWarningFeatureFlag,
    } = this.props;

    // If the user attempts to send while attachments are uploading, setup a Composer banner by setting attemptedSendDraftAttachments and return.
    if (attachments.some((a) => a.status === AttachmentStatusesEnum.DRAFT && !a.hasFailed)) {
      this.setState({attemptedSendDraftAttachments: true});
      return;
    }

    const warnings = findSendWarnings(
      message,
      recipients,
      attachments,
      shouldWarnManyRecipients,
      shouldWarnMissingAttachments,
      this.state.rawVariables,
      hasRawVariablesWarningFeatureFlag,
    );

    if (warnings.length > 0) {
      this.logWarningsDisplay(warnings);
      const sendAttempt: ComposerSendAttempt = {warnings, sendOptions};
      this.setState({sendAttempt});
      return;
    }

    sendMessage(sendOptions);
  };

  componentDidMount() {
    const attachExternalResourcesAsync = async () => {
      const shareFiles = await fetchMostRecentExternalShareFiles();
      if (shareFiles) {
        shareFiles.forEach((file) => this.props.onFileUpload(new File([file.data], file.name)));
      }
    };

    if (this.props.shouldAttachExternalResources) {
      attachExternalResourcesAsync();
    }
  }

  /*
   * Render.
   */

  render() {
    return (
      <LightUserThemeProvider>
        <MessageBase
          renderBanner={({isRounded}) => maybeRenderBanner(this.props, isRounded)}
          isPopout={this.props.isComposerPopout || this.props.isComposerStandalone}
          isPopup={this.props.isComposerPopup}
        >
          {this.renderComposer()}
          {this.maybeRenderWarnings()}
        </MessageBase>
      </LightUserThemeProvider>
    );
  }

  private readonly onEditorPaste: Handler = () => {
    trackInterfaceEvent(InterfaceEventsEnum.COMPOSER_COPIED_MESSAGE_INSERT, {
      is_reply: Boolean(this.props.message.inReplyToId),
      is_shared: this.props.isMessageShared,
      draft_id: this.props.message.id,
    });
  };

  private renderComposer() {
    const {attemptedSendDraftAttachments, rawVariables} = this.state;
    const hasRawVariables = rawVariables.length > 0;

    const composerProps = {
      ...this.props,
      key: this.props.message.uid,
      sendMessage: this.sendMessage,
      dismissAttemptedSendDraftAttachments: this.dismissAttemptedSendDraftAttachments,
      attemptedSendDraftAttachments,
      onPaste: this.onEditorPaste,
    };

    switch (this.props.message.visualType) {
      case MessageVisualTypesEnum.EMAIL:
        return (
          <EmailComposerContainer
            {...composerProps}
            hasRawVariables={hasRawVariables}
            onRawVariables={this.onRawVariables}
            onNewRawVariable={this.onNewRawVariable}
          />
        );

      case MessageVisualTypesEnum.TWEET:
        return <TweetComposer {...composerProps} />;

      case MessageVisualTypesEnum.CHAT:
        return <ChatComposer {...composerProps} />;

      default:
        throw new Error('This should not happen.');
    }
  }

  private maybeRenderWarnings() {
    const {sendAttempt} = this.state;
    if (!sendAttempt || sendAttempt.warnings.length === 0) {
      return null;
    }

    const onRequestClose = () => this.onWarningsRequestClose(sendAttempt, WarningClickActionsEnum.CANCEL);

    const onOkClick = () => this.onWarningsSendAnywayClick(sendAttempt);

    return (
      <ComposerWarningsDialog
        warnings={sendAttempt.warnings}
        onRequestClose={onRequestClose}
        onOkClick={onOkClick}
      />
    );
  }

  private logWarningsDisplay(warnings: readonly MessageWarning[]) {
    for (const warning of warnings) {
      trackInterfaceEvent(InterfaceEventsEnum.COMPOSER_MODAL_VIEW, this.buildBaseWarningLog(warning.type));
    }
  }

  private logWarningsDismiss(warnings: readonly MessageWarning[], action: WarningClickActionsEnum) {
    for (const warning of warnings) {
      trackInterfaceEvent(InterfaceEventsEnum.COMPOSER_MODAL_CLICK, {
        ...this.buildBaseWarningLog(warning.type),
        action,
      });
    }
  }

  private buildBaseWarningLog(warningType: MessageWarningTypesEnum) {
    return {
      namespace: this.props.channel.namespace,
      draft_id: this.props.message.id,
      conversation_id: this.props.conversation?.id || null,
      type: 'warning',
      object: computeLoggingWarningType(warningType),
    };
  }
}

function computeLoggingWarningType(
  warningType: MessageWarningTypesEnum,
): ConversationComposerWarningDialogTypesEnum {
  switch (warningType) {
    case MessageWarningTypesEnum.NO_SUBJECT:
      return ConversationComposerWarningDialogTypesEnum.NO_SUBJECT;

    case MessageWarningTypesEnum.NO_ATTACHMENTS:
      return ConversationComposerWarningDialogTypesEnum.NO_ATTACHMENTS;

    case MessageWarningTypesEnum.MANY_RECIPIENTS:
      return ConversationComposerWarningDialogTypesEnum.MANY_RECIPIENTS;

    case MessageWarningTypesEnum.FAILED_ATTACHMENTS:
      return ConversationComposerWarningDialogTypesEnum.FAILED_ATTACHMENTS;

    case MessageWarningTypesEnum.UPLOADING_ATTACHMENTS:
      return ConversationComposerWarningDialogTypesEnum.UPLOADING_ATTACHMENTS;

    case MessageWarningTypesEnum.HAS_RAW_VARIABLES:
      return ConversationComposerWarningDialogTypesEnum.RAW_VARIABLES;

    default:
      throw new UnreachableError(warningType);
  }
}

function maybeRenderBanner(props: ComposerProps, isRounded: boolean) {
  const {message, author, shareMessage, isMessageShared} = props;

  // The message is shared, let's display a blue banner to remind the user.
  if (author && isMessageShared && message.status === MessageStatusesEnum.DRAFT_COMPOSE) {
    return (
      <SharedDraftMessageBannerContainer
        author={author}
        message={message}
        panelId={props.panelId}
        onRequestClose={props.onRequestClose}
        onPopout={props.onPopout}
        isPopout={props.isComposerPopout}
        isPopup={props.isComposerPopup}
        isRounded={isRounded}
      />
    );
  }

  if (message.status === MessageStatusesEnum.DRAFT_COMPOSE) {
    // The message is a draft, let's display a banner to remind the user.
    return (
      <DraftMessageBannerContainer
        message={message}
        panelId={props.panelId}
        shareMessage={shareMessage}
        onRequestClose={props.onRequestClose}
        onPopout={props.onPopout}
        isPopout={props.isComposerPopout}
        isPopup={props.isComposerPopup}
        isRounded={isRounded}
      />
    );
  }

  return null;
}
